import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pill, Plus, Trash2, Loader2, Save, Pencil, Eye, Sparkles, Link2, CheckCircle2, XCircle, Globe, Clock, Activity } from "lucide-react";
import SupplementProductDetail, { formatProductTitle } from "@/components/supplements/SupplementProductDetail";
import {
  TIMINGS,
  TIMING_COLORS,
  normalizePlanItem,
  normalizePlan,
} from "@/lib/supplementTiming";

const MAX_BULK_IMPORT_URLS = 15;

const parseImportUrls = (text) => {
  const seen = new Set();
  const urls = [];
  for (const part of text.split(/[\n,;]+/)) {
    const url = part.trim();
    if (!url || !/^https?:\/\//i.test(url) || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  return urls;
};

const draftToPayload = (draft) => ({
  name: draft.name || "",
  brand: draft.brand || "",
  category: draft.category || "",
  flavor: draft.flavor || "",
  description: draft.description || "",
  benefits: draft.benefits || "",
  ingredients: draft.ingredients || "",
  nutrition_facts: draft.nutrition_facts || "",
  allergens: draft.allergens || "",
  usage_instructions: draft.usage_instructions || "",
  serving_size: draft.serving_size || "",
  servings_per_container: draft.servings_per_container != null && draft.servings_per_container !== ""
    ? Number(draft.servings_per_container)
    : null,
  unit: draft.unit || "dose",
  price_hint: draft.price_hint || "",
  product_url: draft.product_url || "",
  image_url: draft.image_url || "",
});

const PlanItemSchedule = ({ item }) => {
  const timing = item.timing?.trim() || "Matin";
  return (
    <div className="flex flex-col items-end gap-1.5 shrink-0 text-right min-w-[9.5rem]">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Quantité</p>
        <p className="text-sm font-semibold text-slate-800">{item.dosage || "—"}</p>
      </div>
      <Badge className={`text-xs gap-1 border ${TIMING_COLORS[timing] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
        <Clock className="w-3 h-3" />
        {timing}
      </Badge>
    </div>
  );
};

const CATEGORIES = [
  "BCAA",
  "Protéines",
  "Créatine",
  "Vitamines & minéraux",
  "Oméga-3",
  "Pré-workout",
  "Post-workout",
  "Santé digestive",
  "Sommeil & récupération",
  "Autre",
];

const emptyCatalogForm = () => ({
  name: "",
  brand: "",
  category: "",
  flavor: "",
  description: "",
  benefits: "",
  ingredients: "",
  nutrition_facts: "",
  allergens: "",
  usage_instructions: "",
  serving_size: "",
  servings_per_container: "",
  unit: "dose",
  price_hint: "",
  product_url: "",
  image_url: "",
});

const emptyItem = () => ({ name: "", dosage: "1 dose", timing: "Matin", notes: "", supplement_id: null });

export default function Supplements() {
  const [catalog, setCatalog] = useState([]);
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalogForm, setCatalogForm] = useState(emptyCatalogForm());
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [aiImporting, setAiImporting] = useState(false);
  const [aiDraftReady, setAiDraftReady] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkUrlsText, setBulkUrlsText] = useState("");
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, url: "" });
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkMode, setBulkMode] = useState("urls");
  const [siteUrl, setSiteUrl] = useState("");
  const [siteDiscovering, setSiteDiscovering] = useState(false);
  const [discoveryMeta, setDiscoveryMeta] = useState(null);
  const [planForm, setPlanForm] = useState({ student_id: "", title: "", notes: "", items: [emptyItem()] });
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedDietPlanId, setSelectedDietPlanId] = useState("");
  const [aiPlanGenerating, setAiPlanGenerating] = useState(false);
  const [aiPlanReady, setAiPlanReady] = useState(false);
  const [regeneratingPlanId, setRegeneratingPlanId] = useState(null);
  const [adherenceOverview, setAdherenceOverview] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    const requests = [
      { label: "catalogue", path: "/supplements/catalog", apply: (data) => setCatalog(data || []) },
      { label: "plans compléments", path: "/supplements/plans", apply: (data) => setPlans((data || []).map(normalizePlan)) },
      { label: "élèves", path: "/students", apply: (data) => setStudents(data || []) },
      { label: "plans alimentaires", path: "/diet-plans", apply: (data) => setDietPlans(data || []) },
      { label: "adhérence compléments", path: "/supplements/adherence/overview", apply: (data) => setAdherenceOverview(data || []) },
    ];

    const results = await Promise.allSettled(requests.map((r) => api.get(r.path)));
    const failures = [];

    results.forEach((result, index) => {
      const { label, apply } = requests[index];
      if (result.status === "fulfilled") {
        apply(result.value.data);
      } else {
        const err = result.reason;
        const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || "erreur inconnue";
        failures.push(`${label} (${detail})`);
      }
    });

    if (failures.length > 0) {
      toast.error(`Erreur de chargement : ${failures.join(" · ")}`);
    }

    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const openNewCatalog = () => {
    setEditingCatalog(null);
    setCatalogForm(emptyCatalogForm());
    setImportUrl("");
    setAiDraftReady(false);
    setCatalogOpen(true);
  };

  const applyDraftToForm = (draft) => {
    const payload = draftToPayload(draft);
    setCatalogForm({
      ...payload,
      servings_per_container: draft.servings_per_container ?? "",
    });
    setAiDraftReady(true);
  };

  const openBulkImport = (mode = "urls") => {
    setBulkMode(mode);
    setBulkUrlsText("");
    setSiteUrl("");
    setBulkResults([]);
    setDiscoveryMeta(null);
    setBulkProgress({ current: 0, total: 0, url: "" });
    setBulkImportOpen(true);
  };

  const handleSiteDiscover = async () => {
    const url = siteUrl.trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      toast.error("Collez l'URL du site (https://...)");
      return;
    }

    setSiteDiscovering(true);
    setBulkResults([]);
    setDiscoveryMeta(null);
    setBulkUrlsText("");

    try {
      const { data } = await api.post("/supplements/catalog/discover-from-site", { site_url: url });
      setDiscoveryMeta({
        discovered: data.discovered_count ?? 0,
        skipped: data.skipped_existing ?? 0,
        scanned: data.candidates_scanned ?? 0,
        siteBrand: data.site_brand || null,
      });

      const urls = data.product_urls || [];
      if (urls.length === 0) {
        toast.info(
          data.skipped_existing
            ? `Aucun nouveau produit — ${data.skipped_existing} déjà dans votre catalogue`
            : "Aucun complément détecté sur ce site"
        );
        return;
      }

      setBulkUrlsText(urls.join("\n"));
      toast.success(
        `${urls.length} nouveau${urls.length > 1 ? "x" : ""} produit${urls.length > 1 ? "s" : ""} trouvé${urls.length > 1 ? "s" : ""}${
          data.skipped_existing ? ` (${data.skipped_existing} déjà au catalogue)` : ""
        }`
      );
      setSiteDiscovering(false);
      await handleBulkAnalyze(urls);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Découverte impossible");
      setSiteDiscovering(false);
    }
  };

  const handleAiImport = async () => {
    const urls = parseImportUrls(importUrl);
    if (urls.length === 0) {
      toast.error("Collez au moins un lien produit valide (https://...)");
      return;
    }
    if (urls.length > 1) {
      setCatalogOpen(false);
      setBulkUrlsText(urls.join("\n"));
      setBulkResults([]);
      setBulkProgress({ current: 0, total: 0, url: "" });
      setBulkImportOpen(true);
      handleBulkAnalyze(urls);
      return;
    }
    setAiImporting(true);
    try {
      const { data } = await api.post("/supplements/catalog/import-from-url", { product_url: urls[0] });
      applyDraftToForm(data.draft || {});
      toast.success("Fiche préremplie — vérifiez les informations avant d'enregistrer");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Import IA impossible");
    } finally {
      setAiImporting(false);
    }
  };

  const handleBulkAnalyze = async (presetUrls) => {
    const urls = presetUrls || parseImportUrls(bulkUrlsText);
    if (urls.length === 0) {
      toast.error("Collez au moins une URL valide (https://...)");
      return;
    }
    if (urls.length > MAX_BULK_IMPORT_URLS) {
      toast.error(`Maximum ${MAX_BULK_IMPORT_URLS} URLs par import`);
      return;
    }

    setBulkAnalyzing(true);
    setBulkResults([]);
    setBulkProgress({ current: 0, total: urls.length, url: "" });

    const results = [];
    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      setBulkProgress({ current: i + 1, total: urls.length, url });
      try {
        const { data } = await api.post("/supplements/catalog/import-from-url", { product_url: url });
        results.push({
          url,
          success: true,
          selected: true,
          draft: data.draft || {},
        });
      } catch (err) {
        results.push({
          url,
          success: false,
          selected: false,
          error: err.response?.data?.detail || err.response?.data?.message || "Analyse impossible",
        });
      }
      setBulkResults([...results]);
    }

    const ok = results.filter((r) => r.success).length;
    const ko = results.length - ok;
    if (ok > 0) {
      toast.success(`${ok} fiche${ok > 1 ? "s" : ""} analysée${ok > 1 ? "s" : ""}${ko ? `, ${ko} échec${ko > 1 ? "s" : ""}` : ""}`);
    } else {
      toast.error("Aucune fiche n'a pu être analysée");
    }
    setBulkAnalyzing(false);
    setBulkProgress({ current: 0, total: 0, url: "" });
  };

  const toggleBulkResult = (url) => {
    setBulkResults((prev) => prev.map((r) => (r.url === url ? { ...r, selected: !r.selected } : r)));
  };

  const openBulkResultInForm = (result) => {
    if (!result?.draft) return;
    setBulkImportOpen(false);
    setEditingCatalog(null);
    applyDraftToForm(result.draft);
    setImportUrl(result.url);
    setCatalogOpen(true);
  };

  const handleBulkSave = async () => {
    const toSave = bulkResults.filter((r) => r.success && r.selected && r.draft?.name);
    if (toSave.length === 0) {
      toast.error("Sélectionnez au moins une fiche valide à enregistrer");
      return;
    }

    setBulkSaving(true);
    let saved = 0;
    const errors = [];
    for (const item of toSave) {
      try {
        await api.post("/supplements/catalog", draftToPayload(item.draft));
        saved += 1;
      } catch (err) {
        errors.push(item.draft.name || item.url);
      }
    }

    if (saved > 0) {
      toast.success(`${saved} produit${saved > 1 ? "s" : ""} ajouté${saved > 1 ? "s" : ""} au catalogue`);
      setBulkImportOpen(false);
      setBulkUrlsText("");
      setBulkResults([]);
      fetchAll();
    }
    if (errors.length > 0) {
      toast.error(`Échec pour : ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? "…" : ""}`);
    }
    setBulkSaving(false);
  };

  const openEditCatalog = (sup) => {
    setEditingCatalog(sup);
    setImportUrl(sup.product_url || "");
    setAiDraftReady(false);
    setCatalogForm({
      name: sup.name || "",
      brand: sup.brand || "",
      category: sup.category || "",
      flavor: sup.flavor || "",
      description: sup.description || "",
      benefits: sup.benefits || "",
      ingredients: sup.ingredients || "",
      nutrition_facts: sup.nutrition_facts || "",
      allergens: sup.allergens || "",
      usage_instructions: sup.usage_instructions || "",
      serving_size: sup.serving_size || "",
      servings_per_container: sup.servings_per_container ?? "",
      unit: sup.unit || "dose",
      price_hint: sup.price_hint || "",
      product_url: sup.product_url || "",
      image_url: sup.image_url || "",
    });
    setCatalogOpen(true);
  };

  const handleSaveCatalog = async (e) => {
    e.preventDefault();
    if (!catalogForm.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...catalogForm,
        servings_per_container: catalogForm.servings_per_container
          ? Number(catalogForm.servings_per_container)
          : null,
      };
      if (editingCatalog) {
        await api.put(`/supplements/catalog/${editingCatalog.supplement_id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/supplements/catalog", payload);
        toast.success("Produit ajouté au catalogue");
      }
      setCatalogOpen(false);
      setCatalogForm(emptyCatalogForm());
      setEditingCatalog(null);
      setImportUrl("");
      setAiDraftReady(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCatalog = async (id) => {
    if (!window.confirm("Supprimer ce produit du catalogue ?")) return;
    try {
      await api.delete(`/supplements/catalog/${id}`);
      toast.success("Supprimé");
      fetchAll();
    } catch {
      toast.error("Erreur");
    }
  };

  const openNewPlan = () => {
    setEditingPlan(null);
    setPlanForm({ student_id: "", title: "", notes: "", items: [emptyItem()] });
    setSelectedDietPlanId("");
    setAiPlanReady(false);
    setPlanOpen(true);
  };

  const studentDietPlans = dietPlans.filter((p) => p.student_id === planForm.student_id);

  const handleStudentChangeForPlan = (studentId) => {
    setPlanForm((f) => ({ ...f, student_id: studentId }));
    setSelectedDietPlanId("");
    setAiPlanReady(false);
  };

  const handleAiGeneratePlan = async () => {
    if (!planForm.student_id) {
      toast.error("Choisissez d'abord un élève");
      return;
    }
    if (!selectedDietPlanId) {
      toast.error("Sélectionnez un plan alimentaire");
      return;
    }
    if (catalog.length === 0) {
      toast.error("Ajoutez d'abord des produits à votre catalogue");
      return;
    }

    setAiPlanGenerating(true);
    try {
      const { data } = await api.post("/supplements/plans/generate-from-diet", {
        student_id: planForm.student_id,
        diet_plan_id: selectedDietPlanId,
      });
      const draft = data.draft || {};
      setPlanForm((f) => ({
        ...f,
        title: draft.title || f.title,
        notes: draft.notes || f.notes,
        items: draft.items?.length
          ? draft.items.map((item) => normalizePlanItem(item))
          : f.items,
      }));
      setAiPlanReady(true);
      toast.success("Plan compléments généré — vérifiez avant d'enregistrer");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Génération IA impossible");
    } finally {
      setAiPlanGenerating(false);
    }
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      student_id: plan.student_id,
      title: plan.title,
      notes: plan.notes || "",
      items: plan.items?.length ? plan.items.map((i) => normalizePlanItem(i)) : [emptyItem()],
    });
    setPlanOpen(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (!planForm.student_id || !planForm.title.trim()) {
      toast.error("Élève et titre requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        student_id: planForm.student_id,
        title: planForm.title,
        notes: planForm.notes,
        items: planForm.items.filter((i) => i.name.trim()).map((i) => normalizePlanItem(i)),
      };
      if (editingPlan) {
        await api.put(`/supplements/plans/${editingPlan.plan_id}`, payload);
        toast.success("Plan mis à jour");
      } else {
        await api.post("/supplements/plans", payload);
        toast.success("Plan créé");
      }
      setPlanOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Supprimer ce plan ?")) return;
    try {
      await api.delete(`/supplements/plans/${id}`);
      toast.success("Plan supprimé");
      fetchAll();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleRegenerateTimings = async (plan) => {
    if (!plan?.plan_id) return;
    setRegeneratingPlanId(plan.plan_id);
    try {
      const items = (plan.items || []).filter((i) => i.name?.trim()).map((i) => normalizePlanItem(i));
      if (items.length === 0) {
        toast.error("Ce plan ne contient aucun complément à normaliser");
        return;
      }
      await api.put(`/supplements/plans/${plan.plan_id}`, {
        student_id: plan.student_id,
        title: plan.title,
        notes: plan.notes || "",
        items,
      });
      toast.success("Horaires normalisés et enregistrés");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Impossible de régénérer les horaires");
    } finally {
      setRegeneratingPlanId(null);
    }
  };

  const applyRegenerateTimingsToForm = () => {
    setPlanForm((f) => ({
      ...f,
      items: f.items.map((item) => normalizePlanItem(item)),
    }));
    toast.success("Horaires recalculés — vérifiez puis enregistrez");
  };

  const addItem = () => setPlanForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setPlanForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => setPlanForm((f) => ({
    ...f,
    items: f.items.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
  }));

  const pickFromCatalog = (idx, supId) => {
    const sup = catalog.find((s) => s.supplement_id === supId);
    if (sup) {
      updateItem(idx, "name", formatProductTitle(sup));
      updateItem(idx, "supplement_id", sup.supplement_id);
      updateItem(idx, "dosage", sup.serving_size ? `1 ${sup.serving_size}` : `1 ${sup.unit || "dose"}`);
    }
  };

  const setCatalogField = (field, value) => setCatalogForm((f) => ({ ...f, [field]: value }));

  return (
    <div data-testid="supplements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center">
            <Pill className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Compléments</h1>
            <p className="text-slate-400 mt-1 text-sm">Catalogue produits (marque + fiche) et plans élèves</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="catalog" className="mt-6">
        <TabsList>
          <TabsTrigger value="catalog">Catalogue produits</TabsTrigger>
          <TabsTrigger value="plans">Plans élèves</TabsTrigger>
          <TabsTrigger value="adherence">Adhérence</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-4">
          <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
            <p className="text-sm text-slate-500">
              Créez des fiches produit complètes (ex. BCAA 2:1:1 — Body&Fit) à réutiliser dans vos plans.
            </p>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <Button type="button" variant="outline" onClick={() => openBulkImport("site")} className="rounded-full gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <Globe className="w-4 h-4" /> Parcourir un site
              </Button>
              <Button type="button" variant="outline" onClick={() => openBulkImport("urls")} className="rounded-full gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50">
                <Sparkles className="w-4 h-4" /> Import IA multiple
              </Button>
              <Button onClick={openNewCatalog} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full gap-1.5">
                <Plus className="w-4 h-4" /> Nouveau produit
              </Button>
            </div>
          </div>
          {loading ? (
            <p className="text-slate-400 text-sm">Chargement...</p>
          ) : catalog.length === 0 ? (
            <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
              Aucun produit dans le catalogue. Ajoutez votre premier complément recommandé.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {catalog.map((sup) => (
                <Card key={sup.supplement_id} className="border-slate-100 overflow-hidden">
                  <CardContent className="p-0">
                    {sup.image_url ? (
                      <img src={sup.image_url} alt={sup.name} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-indigo-50 flex items-center justify-center">
                        <Pill className="w-10 h-10 text-indigo-300" />
                      </div>
                    )}
                    <div className="p-4 space-y-3">
                      <div>
                        <p className="font-semibold text-slate-900">{sup.name}</p>
                        {sup.brand && <p className="text-sm text-indigo-600">{sup.brand}</p>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {sup.category && <Badge variant="secondary" className="text-[10px]">{sup.category}</Badge>}
                        {sup.serving_size && <Badge variant="outline" className="text-[10px]">{sup.serving_size}</Badge>}
                        {sup.price_hint && <Badge variant="outline" className="text-[10px]">{sup.price_hint}</Badge>}
                      </div>
                      {sup.description && (
                        <p className="text-xs text-slate-500 line-clamp-2">{sup.description}</p>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => { setViewingProduct(sup); setDetailOpen(true); }}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Fiche
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => openEditCatalog(sup)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteCatalog(sup.supplement_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={openNewPlan} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full gap-1.5">
              <Plus className="w-4 h-4" /> Nouveau plan
            </Button>
          </div>
          {loading ? (
            <p className="text-slate-400 text-sm">Chargement...</p>
          ) : plans.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Aucun plan de compléments</div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <Card key={plan.plan_id} className="border-slate-100">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-900">{plan.title}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{plan.student_name}</p>
                        {plan.notes && <p className="text-xs text-slate-400 mt-1">{plan.notes}</p>}
                      </div>
                      <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                        <Badge variant={plan.is_active ? "default" : "secondary"}>{plan.is_active ? "Actif" : "Inactif"}</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRegenerateTimings(plan)}
                          disabled={regeneratingPlanId === plan.plan_id || !plan.items?.length}
                          className="gap-1"
                        >
                          {regeneratingPlanId === plan.plan_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Clock className="w-3.5 h-3.5" />
                          )}
                          Regénérer horaires
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditPlan(plan)}>Modifier</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePlan(plan.plan_id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {plan.items?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {plan.items.map((item) => (
                          <div key={item.item_id} className="flex items-start gap-3 text-sm bg-slate-50 rounded-lg px-3 py-3">
                            <div className="flex-1 min-w-0">
                              {item.supplement ? (
                                <SupplementProductDetail product={item.supplement} compact />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Pill className="w-4 h-4 text-indigo-500 shrink-0" />
                                  <span className="font-medium text-slate-900">{item.name}</span>
                                </div>
                              )}
                              {item.notes && <p className="text-xs text-slate-500 mt-2 ml-[4.25rem]">{item.notes}</p>}
                            </div>
                            <PlanItemSchedule item={item} />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="adherence" className="mt-4">
          <p className="text-sm text-slate-500 mb-4">
            Pourcentage de jours où l&apos;élève a validé toute sa checklist compléments (30 derniers jours).
          </p>
          {loading ? (
            <p className="text-slate-400 text-sm">Chargement...</p>
          ) : adherenceOverview.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Aucun élève ou aucun plan compléments actif</div>
          ) : (
            <div className="space-y-3">
              {adherenceOverview.map((row) => {
                const pct = row.adherence_percent ?? 0;
                const barColor = pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-red-400";
                return (
                  <Card key={row.student_id} className="border-slate-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                            <Activity className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{row.student_name}</p>
                            <p className="text-xs text-slate-500">
                              {row.days_complete} jour{row.days_complete !== 1 ? "s" : ""} complets sur {row.days_tracked} suivis
                              {row.expected_items > 0 ? ` · ${row.expected_items} compléments/jour` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          <Badge variant={pct >= 70 ? "default" : "secondary"} className="min-w-[3rem] justify-center">
                            {pct}%
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Catalog form dialog */}
      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCatalog ? "Modifier le produit" : "Nouveau produit recommandé"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCatalog} className="space-y-5 mt-2">
            {!editingCatalog && (
              <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-purple-800">
                  <Sparkles className="w-4 h-4 shrink-0" />
                  <p className="text-sm font-medium">Importer avec l&apos;IA</p>
                </div>
                <p className="text-xs text-purple-700/80">
                  Collez une ou plusieurs URLs (une par ligne). L&apos;IA préremplit la fiche — plusieurs liens ouvrent l&apos;import groupé.
                </p>
                <div className="flex gap-2 items-start">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder={"https://www.bodyandfit.com/...\nhttps://www.myprotein.com/..."}
                      rows={3}
                      className="pl-9 bg-white resize-y min-h-[72px]"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAiImport}
                    disabled={aiImporting || !importUrl.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                  >
                    {aiImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Analyser</>}
                  </Button>
                </div>
              </div>
            )}

            {aiDraftReady && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Fiche générée par l&apos;IA — relisez chaque champ (surtout composition et valeurs nutritionnelles) avant d&apos;enregistrer.
              </div>
            )}

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Identité produit</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Nom du produit *</Label>
                  <Input value={catalogForm.name} onChange={(e) => setCatalogField("name", e.target.value)} placeholder="BCAA 2:1:1" required />
                </div>
                <div>
                  <Label>Marque</Label>
                  <Input value={catalogForm.brand} onChange={(e) => setCatalogField("brand", e.target.value)} placeholder="Body&Fit" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={catalogForm.category || "none"} onValueChange={(v) => setCatalogField("category", v === "none" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Saveur</Label>
                  <Input value={catalogForm.flavor} onChange={(e) => setCatalogField("flavor", e.target.value)} placeholder="Citron" />
                </div>
                <div>
                  <Label>Prix indicatif</Label>
                  <Input value={catalogForm.price_hint} onChange={(e) => setCatalogField("price_hint", e.target.value)} placeholder="~24,99 €" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Dosage & format</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Portion</Label>
                  <Input value={catalogForm.serving_size} onChange={(e) => setCatalogField("serving_size", e.target.value)} placeholder="5 g / 1 scoop" />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Input value={catalogForm.unit} onChange={(e) => setCatalogField("unit", e.target.value)} placeholder="scoop, gélule..." />
                </div>
                <div>
                  <Label>Portions / pot</Label>
                  <Input type="number" min="1" value={catalogForm.servings_per_container} onChange={(e) => setCatalogField("servings_per_container", e.target.value)} placeholder="30" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Informations utiles</p>
              <div><Label>Description</Label><Textarea value={catalogForm.description} onChange={(e) => setCatalogField("description", e.target.value)} rows={2} placeholder="Pourquoi ce produit ?" /></div>
              <div><Label>Bénéfices</Label><Textarea value={catalogForm.benefits} onChange={(e) => setCatalogField("benefits", e.target.value)} rows={2} placeholder="Récupération, endurance..." /></div>
              <div><Label>Mode d'emploi</Label><Textarea value={catalogForm.usage_instructions} onChange={(e) => setCatalogField("usage_instructions", e.target.value)} rows={2} placeholder="Mélanger 1 scoop dans 300 ml d'eau..." /></div>
              <div><Label>Composition / ingrédients</Label><Textarea value={catalogForm.ingredients} onChange={(e) => setCatalogField("ingredients", e.target.value)} rows={3} placeholder="L-Leucine, L-Isoleucine, L-Valine..." /></div>
              <div><Label>Valeurs nutritionnelles</Label><Textarea value={catalogForm.nutrition_facts} onChange={(e) => setCatalogField("nutrition_facts", e.target.value)} rows={3} placeholder="Par portion : 5 g BCAA, 0 g sucre..." /></div>
              <div><Label>Allergènes</Label><Input value={catalogForm.allergens} onChange={(e) => setCatalogField("allergens", e.target.value)} placeholder="Lait, soja..." /></div>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Liens</p>
              <div><Label>URL produit (achat)</Label><Input value={catalogForm.product_url} onChange={(e) => setCatalogField("product_url", e.target.value)} placeholder="https://..." /></div>
              <div><Label>Image produit (URL)</Label><Input value={catalogForm.image_url} onChange={(e) => setCatalogField("image_url", e.target.value)} placeholder="https://..." /></div>
            </div>

            <Button type="submit" disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCatalog ? "Enregistrer" : "Ajouter au catalogue"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk AI import dialog */}
      <Dialog open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Import IA — plusieurs produits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Tabs value={bulkMode} onValueChange={setBulkMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="urls" className="gap-1.5"><Link2 className="w-3.5 h-3.5" /> URLs produits</TabsTrigger>
                <TabsTrigger value="site" className="gap-1.5"><Globe className="w-3.5 h-3.5" /> Parcourir un site</TabsTrigger>
              </TabsList>

              <TabsContent value="urls" className="mt-4 space-y-3">
                <p className="text-sm text-slate-500">
                  Collez jusqu&apos;à {MAX_BULK_IMPORT_URLS} liens produits (Body&Fit, MyProtein, etc.), un par ligne. L&apos;IA analyse chaque page puis vous enregistrez la sélection.
                </p>
                <Textarea
                  value={bulkUrlsText}
                  onChange={(e) => setBulkUrlsText(e.target.value)}
                  placeholder={"https://www.bodyandfit.com/fr-fr/product/...\nhttps://www.myprotein.com/p/..."}
                  rows={6}
                  disabled={bulkAnalyzing || bulkSaving || siteDiscovering}
                  className="font-mono text-xs"
                />
              </TabsContent>

              <TabsContent value="site" className="mt-4 space-y-3">
                <p className="text-sm text-slate-500">
                  Collez l&apos;URL d&apos;un site (accueil, boutique ou catégorie compléments). L&apos;IA parcourt le site et propose jusqu&apos;à {MAX_BULK_IMPORT_URLS} produits absents de votre catalogue.
                </p>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    placeholder="https://www.bodyandfit.com/fr-fr/"
                    disabled={bulkAnalyzing || bulkSaving || siteDiscovering}
                    className="pl-9 font-mono text-xs"
                  />
                </div>
                {discoveryMeta && (
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-900 space-y-1">
                    <p>
                      <span className="font-medium">{discoveryMeta.discovered}</span> nouveau{discoveryMeta.discovered > 1 ? "x" : ""} produit{discoveryMeta.discovered > 1 ? "s" : ""} détecté{discoveryMeta.discovered > 1 ? "s" : ""}
                      {discoveryMeta.skipped > 0 ? ` · ${discoveryMeta.skipped} déjà au catalogue` : ""}
                    </p>
                    <p className="text-xs text-emerald-800/80">
                      {discoveryMeta.scanned} liens analysés
                      {discoveryMeta.siteBrand ? ` · Marque : ${discoveryMeta.siteBrand}` : ""}
                    </p>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={handleSiteDiscover}
                  disabled={bulkAnalyzing || bulkSaving || siteDiscovering || !siteUrl.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                >
                  {siteDiscovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Globe className="w-4 h-4 mr-1" /> Découvrir les compléments</>}
                </Button>
              </TabsContent>
            </Tabs>

            {siteDiscovering && (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                Exploration du site et détection des produits…
              </div>
            )}

            {bulkAnalyzing && (
              <div className="rounded-xl border border-purple-100 bg-purple-50/60 px-4 py-3 text-sm text-purple-900 space-y-1">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  <span>
                    Analyse {bulkProgress.current}/{bulkProgress.total}
                    {bulkProgress.url ? ` — ${bulkProgress.url.replace(/^https?:\/\//, "").slice(0, 60)}…` : ""}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-purple-100 overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all duration-300"
                    style={{ width: bulkProgress.total ? `${(bulkProgress.current / bulkProgress.total) * 100}%` : "0%" }}
                  />
                </div>
              </div>
            )}
            {bulkResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Résultats</p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {bulkResults.map((result) => (
                    <div
                      key={result.url}
                      className={`rounded-xl border px-3 py-2.5 flex items-start gap-3 ${
                        result.success ? "border-emerald-100 bg-emerald-50/40" : "border-red-100 bg-red-50/40"
                      }`}
                    >
                      {result.success ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        {result.success ? (
                          <>
                            <p className="font-medium text-slate-900 truncate">
                              {result.draft?.name || "Produit"}
                              {result.draft?.brand ? ` — ${result.draft.brand}` : ""}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{result.url}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {result.draft?.category && <Badge variant="secondary" className="text-[10px]">{result.draft.category}</Badge>}
                              {result.draft?.price_hint && <Badge variant="outline" className="text-[10px]">{result.draft.price_hint}</Badge>}
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-red-700">{result.error}</p>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{result.url}</p>
                          </>
                        )}
                      </div>
                      {result.success && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!result.selected}
                              onChange={() => toggleBulkResult(result.url)}
                              className="rounded border-slate-300"
                            />
                            Importer
                          </label>
                          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openBulkResultInForm(result)}>
                            <Pencil className="w-3 h-3 mr-1" /> Éditer
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Relisez les fiches IA avant d&apos;enregistrer (composition, valeurs nutritionnelles). Utilisez « Éditer » pour corriger un produit avant import.
                </p>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {bulkMode === "urls" && (
                <Button
                  type="button"
                  onClick={() => handleBulkAnalyze()}
                  disabled={bulkAnalyzing || bulkSaving || siteDiscovering || !bulkUrlsText.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                >
                  {bulkAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Analyser tout</>}
                </Button>
              )}
              {bulkResults.some((r) => r.success && r.selected) && (
                <Button
                  type="button"
                  onClick={handleBulkSave}
                  disabled={bulkAnalyzing || bulkSaving}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                >
                  {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>Enregistrer {bulkResults.filter((r) => r.success && r.selected).length} produit(s)</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Fiche produit</DialogTitle></DialogHeader>
          {viewingProduct && <SupplementProductDetail product={viewingProduct} />}
        </DialogContent>
      </Dialog>

      {/* Plan dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPlan ? "Modifier le plan" : "Nouveau plan de compléments"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSavePlan} className="space-y-4 mt-2">
            <div>
              <Label>Élève *</Label>
              <Select value={planForm.student_id} onValueChange={handleStudentChangeForPlan} disabled={!!editingPlan}>
                <SelectTrigger><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {!editingPlan && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-indigo-800">
                  <Sparkles className="w-4 h-4 shrink-0" />
                  <p className="text-sm font-medium">Plan IA depuis un plan alimentaire</p>
                </div>
                <p className="text-xs text-indigo-700/80">
                  Sélectionnez un plan diet de l&apos;élève. L&apos;IA propose des compléments issus de votre catalogue, alignés sur les macros, repas et objectifs — pas une liste générique.
                </p>
                <div>
                  <Label>Plan alimentaire de référence</Label>
                  <Select
                    value={selectedDietPlanId || "none"}
                    onValueChange={(v) => {
                      setSelectedDietPlanId(v === "none" ? "" : v);
                      setAiPlanReady(false);
                    }}
                    disabled={!planForm.student_id || aiPlanGenerating}
                  >
                    <SelectTrigger><SelectValue placeholder={planForm.student_id ? "Choisir un plan diet" : "Choisissez d'abord un élève"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {studentDietPlans.map((p) => (
                        <SelectItem key={p.plan_id} value={p.plan_id}>{p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {planForm.student_id && studentDietPlans.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1.5">Aucun plan alimentaire pour cet élève.</p>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleAiGeneratePlan}
                  disabled={aiPlanGenerating || !planForm.student_id || !selectedDietPlanId || catalog.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full w-full sm:w-auto"
                >
                  {aiPlanGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Générer le plan compléments</>}
                </Button>
              </div>
            )}

            {aiPlanReady && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Plan généré par l&apos;IA à partir du plan alimentaire — ajustez dosages, timing et produits avant validation.
              </div>
            )}

            <div><Label>Titre *</Label><Input value={planForm.title} onChange={(e) => setPlanForm((f) => ({ ...f, title: e.target.value }))} required /></div>
            <div><Label>Notes</Label><Textarea value={planForm.notes} onChange={(e) => setPlanForm((f) => ({ ...f, notes: e.target.value }))} rows={2} /></div>
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <Label>Suppléments</Label>
                <div className="flex items-center gap-1">
                  {editingPlan && (
                    <Button type="button" size="sm" variant="outline" onClick={applyRegenerateTimingsToForm} className="h-8 text-xs gap-1">
                      <Clock className="w-3.5 h-3.5" /> Regénérer horaires
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="ghost" onClick={addItem}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="space-y-3">
                {planForm.items.map((item, idx) => (
                  <div key={idx} className="border border-slate-100 rounded-xl p-3 space-y-2">
                    {catalog.length > 0 && (
                      <Select value={item.supplement_id || "none"} onValueChange={(v) => v !== "none" && pickFromCatalog(idx, v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Choisir depuis le catalogue" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Saisie manuelle</SelectItem>
                          {catalog.map((s) => (
                            <SelectItem key={s.supplement_id} value={s.supplement_id}>
                              {formatProductTitle(s)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input placeholder="Nom affiché" value={item.name} onChange={(e) => updateItem(idx, "name", e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-500">Quantité</Label>
                        <Input placeholder="ex: 4 comprimés, 1 scoop" value={item.dosage} onChange={(e) => updateItem(idx, "dosage", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Quand prendre</Label>
                        <Select value={item.timing || "Matin"} onValueChange={(v) => updateItem(idx, "timing", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{TIMINGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Input placeholder="Notes (optionnel)" value={item.notes} onChange={(e) => updateItem(idx, "notes", e.target.value)} />
                    {planForm.items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-500 text-xs">
                        <Trash2 className="w-3 h-3 mr-1" /> Retirer
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full gap-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Enregistrer</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}